import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { EndpointMap, getEndpoint } from './endpointMap';

/**
 * API version types
 */
export type ApiVersion = 'v1' | 'db' | 'v2';

/**
 * Versioned API Client configuration
 */
interface VersionedApiClientConfig {
  baseURL: string;
  defaultVersion?: ApiVersion;
}

/**
 * A client that can handle multiple API versions
 */
export class VersionedApiClient {
  private axios: AxiosInstance;
  private defaultVersion: ApiVersion;

  constructor(config: VersionedApiClientConfig) {
    this.axios = axios.create({
      baseURL: config.baseURL,
    });
    
    // Attach AuthGuard token to all requests
    this.axios.interceptors.request.use((config) => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('authguard_access_token') : null;
        if (token) {
          if (!config.headers) config.headers = {};
          if (!config.headers['Authorization'] && !config.headers['authorization']) {
            (config.headers as any)['Authorization'] = `Bearer ${token}`;
          }
        }
      } catch {}
      return config;
    });

    // Set the default version to always use db mode
    this.defaultVersion = 'db';

    // Add interceptor to handle client_id query parameter
    this.axios.interceptors.request.use((config) => {
      const clientId = localStorage.getItem('selected_client_id');
      const currentVersion = this.getCurrentVersion();
      
      // Set up params object if it doesn't exist
      if (!config.params) {
        config.params = {};
      }
      
      // Only add client_id for v1 endpoints
      if (currentVersion === 'v1' && clientId) {
        config.params.client_id = clientId;
        console.log(`Adding client_id ${clientId} to v1 request: ${config.url}`);
      } 
      // Explicitly remove client_id for db and v2 endpoints
      else if ((currentVersion === 'db' || currentVersion === 'v2') && config.params.client_id) {
        // Delete the client_id parameter if it was accidentally added
        console.log(`Removing client_id parameter from ${currentVersion} request: ${config.url}`);
        delete config.params.client_id;
      }
      
      // Add debug header to track the API version for troubleshooting
      (config.headers as any)['X-API-Version'] = currentVersion;
      
      return config;
    }, error => {
      console.error('Error in request interceptor:', error);
      return Promise.reject(error);
    });
  }

  /**
   * Get the current API version - always returns 'db' (psychodb mode)
   */
  getCurrentVersion(): ApiVersion {
    // Always use db mode (psychodb mode)
    return 'db';
  }

  /**
   * Set the API version - no-op since we always use 'db' mode
   */
  setApiVersion(version: ApiVersion): void {
    // No-op: API version is fixed to 'db' mode
    // Keep this method to maintain backward compatibility with existing code
  }

  /**
   * Make an HTTP request using the correct endpoint for the current API version
   */
  async request<T = any>(
    endpointMap: EndpointMap,
    params: Record<string, string | number> = {},
    config: AxiosRequestConfig = {},
    specificVersion?: ApiVersion
  ): Promise<AxiosResponse<T>> {
    const version = specificVersion || this.getCurrentVersion();
    const endpoint = getEndpoint(endpointMap, version, params);
    
    return this.axios.request<T>({
      ...config,
      url: endpoint
    });
  }

  /**
   * Make a GET request using the correct endpoint for the current API version
   */
  async get<T = any>(
    endpointMap: EndpointMap,
    params: Record<string, string | number> = {},
    config: AxiosRequestConfig = {},
    specificVersion?: ApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.request<T>(endpointMap, params, { ...config, method: 'GET' }, specificVersion);
  }

  /**
   * Make a POST request using the correct endpoint for the current API version
   */
  async post<T = any>(
    endpointMap: EndpointMap,
    params: Record<string, string | number> = {},
    data?: any,
    config: AxiosRequestConfig = {},
    specificVersion?: ApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.request<T>(endpointMap, params, { ...config, method: 'POST', data }, specificVersion);
  }

  /**
   * Make a PUT request using the correct endpoint for the current API version
   */
  async put<T = any>(
    endpointMap: EndpointMap,
    params: Record<string, string | number> = {},
    data?: any,
    config: AxiosRequestConfig = {},
    specificVersion?: ApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.request<T>(endpointMap, params, { ...config, method: 'PUT', data }, specificVersion);
  }

  /**
   * Make a DELETE request using the correct endpoint for the current API version
   */
  async delete<T = any>(
    endpointMap: EndpointMap,
    params: Record<string, string | number> = {},
    config: AxiosRequestConfig = {},
    specificVersion?: ApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.request<T>(endpointMap, params, { ...config, method: 'DELETE' }, specificVersion);
  }
}

// Create a singleton instance
export const versionedApiClient = new VersionedApiClient({
  baseURL: '',  // No base URL needed since endpoints include full paths
  defaultVersion: 'db'
}); 